import { Avatar, Badge, Stack, Typography } from "@mui/material";
import { UnstyledButton } from "Components/Shared/Buttons/UnstyledButton";
import { useNavigate } from "Navigation/useNavigate";
import { appUrl } from "Utils/UrlUtils";
import type { ReactNode } from "react";

export type Props = {
  /** Fallback profile address for handle-less users (merchants, public customers). */
  identityUserPublicID: string;
  handle: string | null | undefined;
  displayName: string | null | undefined;
  avatarUrl?: string;
  /** Small marker overlaid on the avatar (e.g. the merchant storefront badge). */
  avatarBadge?: ReactNode;
  /**
   * Right-hand slot for real controls (approve/delete, unfollow, the actions menu). Rendered
   * OUTSIDE the row's own trigger, so those controls keep their click to themselves.
   */
  action?: ReactNode;
  /**
   * Right-hand slot for decoration describing the row's state — a selection marker, an
   * "already a member" caption. Rendered INSIDE the trigger, so the whole row including the
   * marker is one target. Must contain nothing interactive.
   */
  marker?: ReactNode;
  /**
   * Replaces the default navigate-to-profile click. For pickers (the chat conversation dialog),
   * where the row's job is to choose someone rather than to go and read about them.
   */
  onSelect?: () => void;
  /**
   * Toggle rows only (an onSelect that picks and unpicks): reports what the marker shows to
   * assistive tech, so the row announces itself as pressed instead of as a plain button.
   */
  isSelected?: boolean;
  /**
   * Renders the row inert: no click, no pointer, not tabbable. Note this is NOT the same as
   * omitting onSelect — that falls back to navigating to the profile, which would take a picker's
   * user out of their half-filled dialog.
   */
  isDisabled?: boolean;
};

/**
 * One person in a social list (search results, mutuals, requests, follows): avatar +
 * name + @handle, clicking the row opens the public profile. Type-blind — hosts map their
 * own DTOs onto the primitive props.
 *
 * The row has one trigger, spanning the identity and the `marker` slot; `action` sits outside
 * it and keeps its own clicks. Which of the two slots a host fills is what decides whether the
 * right-hand side is part of the row or a control of its own.
 */
export const SocialUserRow = ({
  identityUserPublicID,
  handle,
  displayName,
  avatarUrl,
  avatarBadge,
  action,
  marker,
  onSelect,
  isSelected,
  isDisabled,
}: Props) => {
  const navigate = useNavigate();

  const identity = (
    <Stack direction="row" alignItems="center" gap={2} minWidth={0}>
      <Badge
        overlap="circular"
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        badgeContent={avatarBadge}
      >
        <Avatar src={avatarUrl} sx={{ width: 40, height: 40 }}>
          {(displayName || handle || "?")[0]?.toUpperCase()}
        </Avatar>
      </Badge>
      <Stack minWidth={0} alignItems="flex-start">
        {/* alignItems flex-start stops cross-axis stretch, so noWrap needs an
            explicit width cap to have something to ellipsize against */}
        <Typography fontWeight={500} noWrap maxWidth="100%">
          {displayName || "-"}
        </Typography>
        {handle && (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            maxWidth="100%"
          >
            @{handle}
          </Typography>
        )}
      </Stack>
    </Stack>
  );

  return (
    <Stack direction="row" alignItems="center" gap={2}>
      {isDisabled ? (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          flex={1}
          minWidth={0}
          sx={{ opacity: 0.6 }}
        >
          {identity}
          {marker}
        </Stack>
      ) : (
        <UnstyledButton
          onClick={() =>
            onSelect
              ? onSelect()
              : navigate(
                  `${appUrl("public-profile")}/${handle ?? identityUserPublicID}`,
                )
          }
          aria-pressed={isSelected}
          sx={{
            cursor: "pointer",
            flex: 1,
            minWidth: 0,
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          {identity}
          {marker}
        </UnstyledButton>
      )}
      {action && (
        <Stack direction="row" alignItems="center" gap={1} flexShrink={0}>
          {action}
        </Stack>
      )}
    </Stack>
  );
};
